import { useState } from "react";

import {
  BreadcrumbProps,
  Classes,
  Colors,
  Breadcrumbs,
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Emoji, EmojiStyle } from "emoji-picker-react";
import { Link, useParams } from "react-router";

import { useWorkspace } from "@/components/helpers/custom-hooks/use-workspace";
import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { AddToCollectionDialog } from "@/components/pages_logged_in/roleplays/AddToCollectionDialog";
import { CreateCollectionDialog } from "@/components/pages_logged_in/roleplays/CreateCollectionDialog";
import {
  buildRoleplayUrl,
  buildScenarioUrl,
  buildSessionUrl,
} from "@/components/pages_logged_in/roleplays/utils";
import { Container } from "@/components/pieces/bootstrap";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { CloneCollectionDialog } from "@/components/pieces/roleplays/CloneCollectionDialog";
import { CloneScenarioDialog } from "@/components/pieces/roleplays/CloneScenarioDialog";
import { RoleplayDeleteConfirm } from "@/components/pieces/roleplays/RoleplayDeleteConfirm";
import { RoleplayLimitBanner } from "@/components/pieces/roleplays/RoleplayLimitBanner";
import { RoleplayMenu } from "@/components/pieces/roleplays/RoleplayMenu";
import { RoleplaySessionMenu } from "@/components/pieces/roleplays/RoleplaySessionMenu";
import { ScenarioDeleteConfirm } from "@/components/pieces/roleplays/ScenarioDeleteConfirm";
import { ScenarioMenu } from "@/components/pieces/roleplays/ScenarioMenu";
import { ScenarioShareManager } from "@/components/pieces/roleplays/sharing/scenario-share/ScenarioShareManager";
import { getUser } from "@/components/session/JwtTokenManager";
import { MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import { VisibilityScope } from "@/graphql/index";
import {
  FullScenario,
  ScenarioModal,
  RoleplayModal,
  FullRoleplaySession,
  RoleplayPage,
  FullRoleplay,
} from "@/types/roleplays";

import { UpdateVisibilityButton } from "./UpdateVisibilityButton";

const ROLEPLAY_DASHBOARD_ROUTE = {
  href: "/roleplays",
  text: "Roleplay Dashboard",
};

interface UseBreadcrumbsProps {
  page: RoleplayPage;
  roleplay?: RoleplayHeaderProps["roleplay"];
  scenario?: FullScenario;
}

function useBreadcrumbs({
  page,
  roleplay,
  scenario,
}: UseBreadcrumbsProps): BreadcrumbProps[] | undefined {
  const { roleplaySlug } = useParams();

  const roleplayText = roleplay?.name || "Loading";

  const roleplayUrl = roleplay?.slug ? buildRoleplayUrl(roleplay.slug) : "#";

  if (page === "roleplayDetail") {
    return [ROLEPLAY_DASHBOARD_ROUTE, { text: "Collection Overview" }];
  }
  if (page === "scenarioDetail") {
    const breadcrumbs: { href?: string; text: string }[] = [
      ROLEPLAY_DASHBOARD_ROUTE,
    ];

    if (roleplaySlug) {
      breadcrumbs.push({
        href: roleplayUrl,
        text: roleplayText,
      });
    }
    breadcrumbs.push({ text: scenario?.name || "Loading" });
    return breadcrumbs;
  }

  if (page === "sessionDetail") {
    const user = getUser();
    const isScenarioOwner = scenario?.owner?.id === user?.id;
    const couldAccessScenarios =
      isScenarioOwner ||
      (scenario?.visibilityScope === VisibilityScope.User && isScenarioOwner) ||
      (scenario?.visibilityScope === VisibilityScope.Workspace &&
        user?.workspaces) ||
      scenario?.visibilityScope === VisibilityScope.Global;
    const baseBreadcrumbs = [
      {
        href: couldAccessScenarios
          ? buildScenarioUrl({
              roleplaySlug: roleplay?.slug,
              scenarioSlug: scenario?.slug ?? "",
            })
          : null,
        text: scenario?.name || "Loading",
      },
      { text: "Session Feedback" },
    ];

    const preRoutes = !roleplaySlug
      ? [
          {
            text: "Scenario Details",
          },
        ]
      : [ROLEPLAY_DASHBOARD_ROUTE, { href: roleplayUrl, text: roleplayText }];

    return [...preRoutes, ...baseBreadcrumbs];
  }
  if (page === "scenarioCreate") {
    const items = [ROLEPLAY_DASHBOARD_ROUTE, { text: "Create Scenario" }];
    if (roleplay) {
      items.splice(1, 0, {
        href: roleplayUrl,
        text: roleplayText,
      });
    }
    return items;
  }
  if (page === "intakeProcessing") {
    return [
      ROLEPLAY_DASHBOARD_ROUTE,
      {
        href: roleplayUrl,
        text: roleplayText,
      },
      { text: "Generating" },
    ];
  }
}

interface HeaderTitleProps {
  page: RoleplayPage;
  roleplay?: RoleplayHeaderProps["roleplay"];
  scenario?: FullScenario;
  roleplaySession?: FullRoleplaySession;
}

function HeaderTitle({
  page,
  roleplay,
  scenario,
  roleplaySession,
}: HeaderTitleProps) {
  if (page === "roleplayDetail") {
    return (
      <Link
        className="color-inherit"
        to={buildRoleplayUrl(roleplay?.slug ?? "")}
      >
        <h1 className="m-0 text-[28px]">{roleplay?.name || "Loading..."}</h1>
      </Link>
    );
  }
  if (page === "scenarioDetail") {
    const text = scenario?.name || "Loading...";
    return roleplay ? (
      <Link
        className="color-inherit"
        to={buildScenarioUrl({
          roleplaySlug: roleplay.slug,
          scenarioSlug: scenario?.slug ?? "",
        })}
      >
        <h1 className="m-0 text-[28px]">{text}</h1>
      </Link>
    ) : (
      <h1 className="m-0 text-[28px]">{text}</h1>
    );
  }
  if (page === "sessionDetail") {
    return (
      <Link
        className="color-inherit"
        to={buildSessionUrl({
          roleplaySlug: roleplay?.slug,
          scenarioSlug: scenario?.slug,
          sessionUuid: roleplaySession?.uuid,
        })}
      >
        <h1 className="m-0 text-[28px]">{scenario?.name || "Loading..."}</h1>
      </Link>
    );
  }
  if (page === "intakeProcessing") {
    return <h1 className="m-0 text-[28px]">Generating Scenario</h1>;
  }

  if (page === "scenarioCreate" && roleplay) {
    return (
      <Link
        className="color-inherit"
        to={buildRoleplayUrl(roleplay?.slug ?? "")}
      >
        <h1 className="m-0 text-[28px]">{roleplay?.name || "Loading..."}</h1>
      </Link>
    );
  }
  return null;
}

function Subheader({
  page,
  roleplaySession,
}: {
  page: RoleplayPage;
  roleplaySession: FullRoleplaySession;
}) {
  if (page === "sessionDetail") {
    if (!roleplaySession || !roleplaySession.createdAt) {
      return (
        <Box mt={2} className={Classes.SKELETON} height={22} width={300} />
      );
    }
    return (
      <Box mt={2} color={MIDTEXT_COLOR}>
        Session on{" "}
        <DateTimeDisplay
          time={roleplaySession.createdAt}
          formatString="MMM do, yyyy 'at' h:mm a"
        />
      </Box>
    );
  }

  return null;
}

const shouldHideMenu = (item: { visibilityScope?: VisibilityScope | null }) => {
  return !item;
};

function RoleplayDetailMenu({
  roleplay,
}: {
  roleplay: {
    visibilityScope?: VisibilityScope | null;
    workspace?: NonNullable<FullRoleplay["workspace"]>;
    owner?: FullRoleplay["owner"] | FullScenario["owner"];
    id: string;
    slug: string;
    name: string;
    canEdit?: boolean;
  };
}) {
  const [currentActiveModal, setCurrentActiveModal] = useState<RoleplayModal>();

  const currentWorkspace = useWorkspace();

  if (
    shouldHideMenu({
      visibilityScope: roleplay?.visibilityScope,
    })
  ) {
    return <Box />;
  }

  const canUpdateVisibilityScope =
    roleplay.workspace?.id === currentWorkspace?.id &&
    isWorkspaceAdmin() &&
    roleplay.visibilityScope !== VisibilityScope.Global;

  return (
    <Flex>
      {canUpdateVisibilityScope && (
        <>
          <UpdateVisibilityButton
            owner={roleplay.owner}
            visibilityScope={roleplay.visibilityScope}
            id={roleplay.id}
            slug={roleplay.slug}
            name={roleplay.name}
            type="roleplay"
            workspace={roleplay.workspace ?? undefined}
          />
          <Box ml={3} />
        </>
      )}
      {roleplay && (
        <RoleplayMenu
          onDelete={() => setCurrentActiveModal("deleteRoleplay")}
          onEdit={() => setCurrentActiveModal("editCollection")}
          canEdit={roleplay?.canEdit ?? false}
          onClone={() => setCurrentActiveModal("cloneCollection")}
        />
      )}
      <CreateCollectionDialog
        isOpen={currentActiveModal === "editCollection"}
        roleplay={roleplay}
        onClose={() => setCurrentActiveModal(undefined)}
        onFinished={() => {
          setCurrentActiveModal(undefined);
        }}
      />
      <RoleplayDeleteConfirm
        open={currentActiveModal === "deleteRoleplay"}
        onClose={() => setCurrentActiveModal(undefined)}
        roleplay={roleplay}
        onFinished={() => {
          setCurrentActiveModal(undefined);
        }}
      />
      <CloneCollectionDialog
        open={currentActiveModal === "cloneCollection"}
        onClose={() => setCurrentActiveModal(undefined)}
        collection={roleplay}
      />
    </Flex>
  );
}

export function ScenarioDetailMenu({
  scenario,
  onEditScenario,
}: {
  scenario: FullScenario;
  onEditScenario: () => void;
}) {
  const [currentActiveModal, setCurrentActiveModal] = useState<ScenarioModal>();
  const workspaceSlug = useWorkspaceSlug();

  if (
    shouldHideMenu({
      visibilityScope: scenario?.visibilityScope,
    })
  ) {
    return <Box />;
  }

  return (
    <div className="flex gap-2">
      {workspaceSlug && scenario?.canUpdatePermission && (
        <ScenarioShareManager scenario={scenario} />
      )}

      <ScenarioMenu
        scenario={scenario}
        onDelete={() => {
          setCurrentActiveModal("deleteScenario");
        }}
        onCloneScenario={() => {
          setCurrentActiveModal("cloneScenario");
        }}
        onAddToCollection={() => {
          setCurrentActiveModal("addToCollection");
        }}
        onEditScenario={onEditScenario}
      />

      <ScenarioDeleteConfirm
        open={currentActiveModal === "deleteScenario"}
        scenario={scenario}
        onClose={() => setCurrentActiveModal(undefined)}
      />
      <CloneScenarioDialog
        open={currentActiveModal === "cloneScenario"}
        scenario={scenario}
        onClose={() => setCurrentActiveModal(undefined)}
      />

      <AddToCollectionDialog
        open={currentActiveModal === "addToCollection"}
        scenario={scenario}
        onClose={() => setCurrentActiveModal(undefined)}
      />
    </div>
  );
}

interface RoleplayHeaderProps {
  page: RoleplayPage;
  roleplay?: {
    id: string;
    name: string;
    slug: string;
    emojiUnicode: string;
  } | null;
  scenario?: FullScenario;
  roleplaySession?: FullRoleplaySession;
  loading?: boolean;
  px?: number;
  py?: number;
  noMargins?: boolean;
  onEditScenario?: () => void;
}

export function RoleplayHeader({
  page,
  px = 0,
  py = 4,
  noMargins = false,
  roleplay,
  scenario,
  roleplaySession,
  loading = false,
  onEditScenario = () => {},
}: RoleplayHeaderProps) {
  const breadcrumbs: BreadcrumbProps[] | undefined = useBreadcrumbs({
    page,
    roleplay,
    scenario,
  });

  function breadcrumbRenderer(props: BreadcrumbProps) {
    if (loading) {
      return <Box className={Classes.SKELETON} height={20} width={83} />;
    }
    return (
      <StyledBreadcrumb title={String(props.text)}>
        {props.href ? <Link to={props.href}>{props.text}</Link> : props.text}
      </StyledBreadcrumb>
    );
  }

  const hasIcon = Boolean(roleplay?.emojiUnicode) || loading;

  return (
    <>
      <Box className="bbs" py={py} bg={Colors.WHITE} px={px}>
        <Container style={noMargins ? { margin: "revert" } : {}}>
          <Box mb={noMargins ? 0 : 2}>
            <Breadcrumbs
              items={breadcrumbs}
              breadcrumbRenderer={breadcrumbRenderer}
            />
          </Box>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center">
              {hasIcon ? (
                <Media greaterThan="xs">
                  {loading || !roleplay ? (
                    <Box className={Classes.SKELETON} height={33} width={26} />
                  ) : (
                    <Emoji
                      unified={roleplay.emojiUnicode.toLowerCase()}
                      emojiStyle={EmojiStyle.APPLE}
                      size={26}
                    />
                  )}
                </Media>
              ) : null}
              <Box ml={hasIcon ? [0, 3] : 0} width="100%">
                <Box
                  fontWeight={600}
                  fontSize={26}
                  lineHeight={1}
                  className={loading ? Classes.SKELETON : ""}
                  width={loading ? 400 : "auto"}
                  height={loading ? 33 : "auto"}
                >
                  <HeaderTitle
                    page={page}
                    roleplay={roleplay}
                    scenario={scenario}
                    roleplaySession={roleplaySession}
                  />
                </Box>
                {roleplaySession && (
                  <Subheader page={page} roleplaySession={roleplaySession} />
                )}
              </Box>
            </Flex>
            <Flex alignSelf={"flex-end"}>
              {page === "roleplayDetail" && roleplay && (
                <RoleplayDetailMenu roleplay={roleplay} />
              )}
              {page === "scenarioDetail" && scenario && onEditScenario && (
                <ScenarioDetailMenu
                  scenario={scenario}
                  onEditScenario={onEditScenario}
                />
              )}
              {page === "sessionDetail" && roleplaySession && (
                <RoleplaySessionMenu
                  roleplaySession={roleplaySession}
                  showAccessButton={true}
                />
              )}
            </Flex>
          </Flex>
        </Container>
      </Box>
      {!loading && <RoleplayLimitBanner page={page} />}
    </>
  );
}

const StyledBreadcrumb = styled(Box)`
  max-width: 200px;
  color: ${SUBTEXT_COLOR};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  font-size: 14px;

  a {
    font-weight: 600;
    color: ${SUBTEXT_COLOR};
  }
`;
